
import { settings } from "../stores/settings.js";
import { AppSettingsEmail } from "./app-settings-email.js";
import { AppSettingsFiles } from "./app-settings-files.js";
import { AppSettingsGithub } from "./app-settings-github.js";
import { AppSettingsGoogle } from "./app-settings-google.js";
import { AppSettingsMemory } from "./app-settings-memory.js";
import { AppSettingsMcp } from "./app-settings-mcp.js";
import { AppSettingsModels } from "./app-settings-models.js";
import { AppSettingsPersonal } from "./app-settings-personal.js";
import { AppSettingsSkills } from "./app-settings-skills.js";
import { AppSettingsTelegram } from "./app-settings-telegram.js";
import { AppSettingsThreads } from "./app-settings-threads.js";

const styles = css`
  :host {
    display: block;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .root {
    height: 100%;
    min-height: 0;
    width: 100%;
  }

  .layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    height: 100%;
    min-height: 0;
  }

  .tabs {
    background: var(--background);
    border-right: 1px solid var(--border);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
    padding: 12px 8px;
  }

  .tab {
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--foreground);
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    min-height: 34px;
    padding: 7px 10px;
    text-align: left;
    transition:
      background-color 140ms ease,
      color 140ms ease;
    white-space: nowrap;
  }

  .tab:hover {
    background: var(--sidebar-accent, var(--accent));
  }

  .tab:focus-visible { box-shadow: 0 0 0 2px var(--ring-shadow, rgb(24 24 27 / 12%)); outline: none; }

  .layout[data-active="connections"] .tab[data-section="connections"],
  .layout[data-active="personal"] .tab[data-section="personal"],
  .layout[data-active="email"] .tab[data-section="email"],
  .layout[data-active="mcp"] .tab[data-section="mcp"],
  .layout[data-active="files"] .tab[data-section="files"],
  .layout[data-active="memories"] .tab[data-section="memories"],
  .layout[data-active="skills"] .tab[data-section="skills"],
  .layout[data-active="threads"] .tab[data-section="threads"],
  .layout[data-active="models"] .tab[data-section="models"] {
    background: var(--sidebar-accent, var(--accent));
    color: var(--foreground);
    font-weight: 600;
  }

  .panels {
    background: var(--background);
    box-sizing: border-box;
    min-width: 0;
    overflow-y: auto;
    padding: 24px;
  }

  .panel {
    display: none;
    flex-direction: column;
    gap: 24px;
    margin: 0 auto;
    max-width: 720px;
    width: 100%;
  }

  .notice { background: var(--accent); border: 1px solid var(--border); border-radius: 6px; color: var(--foreground); font-size: 13px; line-height: 1.4; margin: 0 auto 20px; max-width: 720px; padding: 9px 11px; }
  .notice:empty { display: none; }

  .layout[data-active="connections"] .panel[data-panel="connections"],
  .layout[data-active="personal"] .panel[data-panel="personal"],
  .layout[data-active="email"] .panel[data-panel="email"],
  .layout[data-active="mcp"] .panel[data-panel="mcp"],
  .layout[data-active="files"] .panel[data-panel="files"],
  .layout[data-active="memories"] .panel[data-panel="memories"],
  .layout[data-active="skills"] .panel[data-panel="skills"],
  .layout[data-active="threads"] .panel[data-panel="threads"],
  .layout[data-active="models"] .panel[data-panel="models"] {
    display: flex;
  }

  @media (max-width: 767px) {
    .layout {
      grid-template-rows: auto minmax(0, 1fr);
      grid-template-columns: 1fr;
    }

    .tabs {
      background: var(--background);
      border-bottom: 1px solid var(--border);
      border-right: 0;
      flex-direction: row;
      min-height: 48px;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0 8px;
    }
    .tab { align-self: stretch; border-radius: 0; padding: 0 10px; position: relative; }
    .layout[data-active="connections"] .tab[data-section="connections"]::after,
    .layout[data-active="personal"] .tab[data-section="personal"]::after,
    .layout[data-active="email"] .tab[data-section="email"]::after,
    .layout[data-active="mcp"] .tab[data-section="mcp"]::after,
    .layout[data-active="files"] .tab[data-section="files"]::after,
    .layout[data-active="memories"] .tab[data-section="memories"]::after,
    .layout[data-active="skills"] .tab[data-section="skills"]::after,
    .layout[data-active="threads"] .tab[data-section="threads"]::after,
    .layout[data-active="models"] .tab[data-section="models"]::after { background: var(--foreground); bottom: 0; content: ""; height: 2px; inset-inline: 10px; position: absolute; }
    .panels { padding: 20px 16px 40px; }
  }
`;

export class AppSettings extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettings)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #textStart9;
  #textEnd9;
  #textLast9;
  #textStart10;
  #textEnd10;
  #textLast10;
  #textStart11;
  #textEnd11;
  #textLast11;
  #attr0;
  #attrLast0;
  #storeCleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;



  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    this.#connectStores();
  }

  disconnectedCallback() {
    this.#disconnectStores();
  }


  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="root"><div class="layout" data-active="${this.#escAttr(settings.activeSection)}" data-argon-bind="0"><nav class="tabs" aria-label="Settings sections"><button type="button" class="tab" data-section="personal" data-argon="5236">Personal</button><button type="button" class="tab" data-section="connections" data-argon="5386">Connections</button><button type="button" class="tab" data-section="email" data-argon="5536">Email</button><button type="button" class="tab" data-section="mcp" data-argon="5672">MCP servers</button><button type="button" class="tab" data-section="files" data-argon="5814">Writable folders</button><button type="button" class="tab" data-section="memories" data-argon="5966">Memories</button><button type="button" class="tab" data-section="skills" data-argon="6111">Skills</button><button type="button" class="tab" data-section="models" data-argon="6252">Models</button><button type="button" class="tab" data-section="threads" data-argon="6394">Threads</button></nav><div class="panels"><div class="notice" role="status"><!--argon-text:0-->${this.#escText(settings.notice)}<!--/argon-text:0--></div><section class="panel" data-panel="personal"><app-settings-personal><!--argon-text:1-->${""}<!--/argon-text:1--></app-settings-personal></section><section class="panel" data-panel="connections"><app-settings-telegram><!--argon-text:2-->${""}<!--/argon-text:2--></app-settings-telegram><app-settings-github><!--argon-text:3-->${""}<!--/argon-text:3--></app-settings-github><app-settings-google><!--argon-text:4-->${""}<!--/argon-text:4--></app-settings-google></section><section class="panel" data-panel="email"><app-settings-email><!--argon-text:5-->${""}<!--/argon-text:5--></app-settings-email></section><section class="panel" data-panel="mcp"><app-settings-mcp><!--argon-text:6-->${""}<!--/argon-text:6--></app-settings-mcp></section><section class="panel" data-panel="files"><app-settings-files><!--argon-text:7-->${""}<!--/argon-text:7--></app-settings-files></section><section class="panel" data-panel="memories"><app-settings-memory><!--argon-text:8-->${""}<!--/argon-text:8--></app-settings-memory></section><section class="panel" data-panel="skills"><app-settings-skills><!--argon-text:9-->${""}<!--/argon-text:9--></app-settings-skills></section><section class="panel" data-panel="models"><app-settings-models><!--argon-text:10-->${""}<!--/argon-text:10--></app-settings-models></section><section class="panel" data-panel="threads"><app-settings-threads><!--argon-text:11-->${""}<!--/argon-text:11--></app-settings-threads></section></div></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
        case 'argon-text:9':
          this.#textStart9 = marker;
          break;
        case '/argon-text:9':
          this.#textEnd9 = marker;
          break;
        case 'argon-text:10':
          this.#textStart10 = marker;
          break;
        case '/argon-text:10':
          this.#textEnd10 = marker;
          break;
        case 'argon-text:11':
          this.#textStart11 = marker;
          break;
        case '/argon-text:11':
          this.#textEnd11 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #connectStores() {
    if (this.#storeCleanups.length > 0) return;
    this.#storeCleanups.push(__argonStoreSubscribe0("settings.activeSection", () => this.#updateBindings("store:../stores/settings.js:settings.activeSection")));
    this.#storeCleanups.push(__argonStoreSubscribe0("settings.notice", () => this.#updateBindings("store:../stores/settings.js:settings.notice")));
  }

  #disconnectStores() {
    for (const cleanup of this.#storeCleanups.splice(0)) cleanup();
  }

  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'store:../stores/settings.js:settings.notice') {
      const __argonNext = `${this.#escText(settings.notice)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast9) {
        this.#textLast9 = __argonNext;
        this.#replaceRange(this.#textStart9, this.#textEnd9, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast10) {
        this.#textLast10 = __argonNext;
        this.#replaceRange(this.#textStart10, this.#textEnd10, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast11) {
        this.#textLast11 = __argonNext;
        this.#replaceRange(this.#textStart11, this.#textEnd11, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'store:../stores/settings.js:settings.activeSection') && this.#attr0) {
      const __argonNext = `${settings.activeSection}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-active', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '5236').forEach(el => this.#listen(el, '5236', 'click', () => { settings.activeSection = "personal"; }));
    this.#eventTargets(scope, '5386').forEach(el => this.#listen(el, '5386', 'click', () => { settings.activeSection = "connections"; }));
    this.#eventTargets(scope, '5536').forEach(el => this.#listen(el, '5536', 'click', () => { settings.activeSection = "email"; }));
    this.#eventTargets(scope, '5672').forEach(el => this.#listen(el, '5672', 'click', () => { settings.activeSection = "mcp"; }));
    this.#eventTargets(scope, '5814').forEach(el => this.#listen(el, '5814', 'click', () => { settings.activeSection = "files"; }));
    this.#eventTargets(scope, '5966').forEach(el => this.#listen(el, '5966', 'click', () => { settings.activeSection = "memories"; }));
    this.#eventTargets(scope, '6111').forEach(el => this.#listen(el, '6111', 'click', () => { settings.activeSection = "skills"; }));
    this.#eventTargets(scope, '6252').forEach(el => this.#listen(el, '6252', 'click', () => { settings.activeSection = "models"; }));
    this.#eventTargets(scope, '6394').forEach(el => this.#listen(el, '6394', 'click', () => { settings.activeSection = "threads"; }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
